import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CartSidebar from '@/components/feature/CartSidebar';
import ProductCard from './components/ProductCard';
import { products } from '@/mocks/products';

const ALL_CATEGORIES = ['All', 'Footwear', 'Jackets', 'Tents', 'Backpacks', 'Sleeping Gear', 'Navigation', 'Climbing', 'Lighting', 'Cooking', 'Accessories'];
const SORT_OPTIONS = [
  { value: 'featured', label: 'Featured' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'rating', label: 'Top Rated' },
  { value: 'name', label: 'Name A-Z' },
];

export default function ProductsPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [sortBy, setSortBy] = useState('featured');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = useMemo(() => {
    let list = [...products];
    if (activeCategory !== 'All') {
      list = list.filter((p) => p.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (p) => p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q) || p.description.toLowerCase().includes(q)
      );
    }
    switch (sortBy) {
      case 'price-asc': list.sort((a, b) => a.price - b.price); break;
      case 'price-desc': list.sort((a, b) => b.price - a.price); break;
      case 'rating': list.sort((a, b) => b.rating - a.rating); break;
      case 'name': list.sort((a, b) => a.name.localeCompare(b.name)); break;
      default: break;
    }
    return list;
  }, [activeCategory, sortBy, searchQuery]);

  return (
    <div className="font-inter min-h-screen bg-[#F7F8F9]">
      <Navbar />

      {/* Page Header */}
      <div className="bg-[#1A1A1A] pt-28 pb-14 px-6 md:px-12">
        <p className="font-outfit text-[#C4FF61] text-xs uppercase tracking-[0.2em] mb-2">SWIFTMARKET LLC</p>
        <h1 className="font-outfit font-black text-white text-4xl md:text-5xl leading-none">
          All Outdoor Gear
        </h1>
        <p className="font-inter text-white/50 text-sm mt-3 max-w-md">
          Premium trail-tested equipment for every type of adventure, from desert trails to alpine summits.
        </p>
      </div>

      <div className="px-6 md:px-12 py-10">
        {/* Filters Bar */}
        <div className="flex flex-col lg:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-gray-400 text-sm"></i>
            </div>
            <input
              type="text"
              placeholder="Search gear..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-full pl-10 pr-4 py-2.5 text-sm font-inter text-[#1A1A1A] placeholder-gray-400 focus:outline-none focus:border-[#C4FF61] transition-colors"
            />
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-outfit text-gray-400 uppercase tracking-wider whitespace-nowrap">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-white border border-gray-200 rounded-full px-4 py-2.5 text-sm font-inter text-[#1A1A1A] focus:outline-none focus:border-[#C4FF61] transition-colors cursor-pointer appearance-none pr-8"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="ml-auto">
            <span className="text-sm font-inter text-gray-400">{filtered.length} products</span>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {ALL_CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-outfit font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-[#1A1A1A] text-white'
                  : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-product-shop>
            {filtered.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="w-16 h-16 flex items-center justify-center">
              <i className="ri-search-line text-5xl text-gray-300"></i>
            </div>
            <p className="font-outfit font-semibold text-gray-400 text-lg">No products found</p>
            <p className="font-inter text-sm text-gray-400">Try adjusting your search or filter criteria</p>
            <button
              onClick={() => { setActiveCategory('All'); setSearchQuery(''); }}
              className="mt-2 bg-[#1A1A1A] text-white text-sm font-outfit font-medium px-6 py-3 rounded-full hover:bg-[#2A2A2A] transition-colors cursor-pointer whitespace-nowrap"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      <Footer />
      <CartSidebar />
    </div>
  );
}
